import { useRef, type CSSProperties, type Key, type ReactNode } from 'react';
import { StyleObject, styled, useStyletron } from '@visualfabriq/vf-ui-kit';
import { useVirtualizer, useWindowVirtualizer, VirtualItem } from '@tanstack/react-virtual';

export type VirtualListProps<T> = {
  items: T[];
  estimateSize: number;
  overscan?: number;
  gap?: number;
  getItemKey?: (item: T, index: number) => Key;
  style?: CSSProperties;
  className?: string;
  renderEmpty?: () => ReactNode;

  /** render-prop */
  children: (item: T, index: number, virtualItem: VirtualItem) => ReactNode;
};

export function VirtualList<T>(props: VirtualListProps<T>) {
  const { items, children, renderEmpty, estimateSize, overscan = 5, gap = 0, className, getItemKey } = props;

  const scrollRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => estimateSize,
    getItemKey: getItemKey ? (index) => getItemKey(items[index]!, index) : undefined,
    overscan,
    gap,
    // keep if you want
    useFlushSync: false,
  });

  const virtualItems = virtualizer.getVirtualItems();

  return (
    <ScrollContainer ref={scrollRef} className={className}>
      {items.length === 0 ? (
        renderEmpty?.()
      ) : (
        <ViewportContainer style={{ height: `${virtualizer.getTotalSize()}px` }} role="list">
          {virtualItems.map((virtualItem) => {
            const item = items[virtualItem.index]!;
            return (
              <ItemWrapper
                key={virtualItem.key}
                data-index={virtualItem.index}
                ref={virtualizer.measureElement}
                role="listitem"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  transform: `translateY(${virtualItem.start}px)`,
                }}
              >
                {children(item, virtualItem.index, virtualItem)}
              </ItemWrapper>
            );
          })}
        </ViewportContainer>
      )}
    </ScrollContainer>
  );
}

export type WindowVirtualListProps<T> = {
  items: readonly T[];
  children: (item: T, index: number) => React.ReactNode;
  renderEmpty?: () => React.ReactNode;
  estimateSize: number;
  overscan?: number;
  gap?: number;
  scrollMargin?: number;
  style?: StyleObject;
  className?: string;
};

export function WindowVirtualList<T>(props: WindowVirtualListProps<T>) {
  const { items, renderEmpty, estimateSize, overscan = 5, gap = 0, scrollMargin = 0, style = {}, className } = props;
  const [css] = useStyletron();

  const virtualizer = useWindowVirtualizer({
    count: items.length,
    estimateSize: () => estimateSize,
    overscan,
    gap,
    scrollMargin,
    useFlushSync: false,
  });

  const virtualItems = virtualizer.getVirtualItems();

  if (items.length === 0) {
    return <>{renderEmpty?.()}</>;
  }

  return (
    <ViewportContainer
      style={{ height: `${virtualizer.getTotalSize()}px` }}
      role="list"
      className={[className, css(style)].filter(Boolean).join(' ')}
    >
      <ItemsContainer style={{ transform: `translateY(${virtualItems[0]?.start ?? 0}px)` }}>
        {virtualItems.map((virtualItem) => {
          const item = items[virtualItem.index]!;
          return (
            <ItemWrapper
              key={virtualItem.key}
              data-index={virtualItem.index}
              ref={virtualizer.measureElement}
              role="listitem"
            >
              {props.children(item, virtualItem.index)}
            </ItemWrapper>
          );
        })}
      </ItemsContainer>
    </ViewportContainer>
  );
}

const ScrollContainer = styled('div', () => ({
  overflow: 'auto',
  contain: 'layout paint',
  overscrollBehavior: 'contain',
}));

const ViewportContainer = styled('div', () => ({
  width: '100%',
  position: 'relative',
}));

const ItemsContainer = styled('div', () => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
}));

const ItemWrapper = styled('div', () => ({
  minWidth: 0,
}));
